import {HttpClient} from '@angular/common/http'
import {inject, Injectable, signal} from '@angular/core'
import {BehaviorSubject, forkJoin, map, Observable, switchMap} from 'rxjs'
import {environment} from '../../environments/environment'
import {
  IAdvice,
  ICustomer,
  IDocument,
  IImage,
  IReport,
  IRiscToleranceQuestionnaire,
  ISignedUrl,
  TImageId
} from '../application/types'
import {
  ChartBaseComponent
} from '../components/charts/chart-base/chart-base.component'
import {
  SavingsGoalSummaryComponent
} from '../summary/savings-goal-summary/savings-goal-summary.component'

/**
 * Temp interface, maybe move this to "BE" in the future
 */
export interface IPrintResult {
  // A view URL
  url: string
  // The ID if you want to process it further.
  id: string
}

export const getDefaultReport = (): IReport => (
  {
    customer: {name: '', mobilePhone: '', personalNumber: '', email: ''},
    fundFactSheetIds: [],
    includeAllFundFactSheets: false,
    advisor: {name: 'Dr. Jane Smith', sId: '', office: ''},
    savingsGoals: [],
    advisorNotes: '',
    rtqAnswers: {} as IRiscToleranceQuestionnaire
  }
)

@Injectable({
  providedIn: 'root'
})
export class DocumentService {

  // We use this to notify all components who have graphs, to generate and send them here.
  public timeToPrint = new BehaviorSubject<boolean>(false)

  public report: IReport = getDefaultReport()
  public graphs: ChartBaseComponent[] = []
  public savingsGoals: SavingsGoalSummaryComponent[] = []
  public documentUrl = signal<string | null>(null)
  public document: IPrintResult | null = null
  public generatingDocument = signal<boolean>(false)
  public images: IImage[] = []

  private readonly http = inject(HttpClient)

  public generateDocument(advice: IAdvice, customer: ICustomer, funFactsSheetIds: string[], rtq: IRiscToleranceQuestionnaire) {
    this.document = null
    this.generatingDocument.set(true)
    this.report.advisor = advice.advisor
    this.report.fundFactSheetIds = funFactsSheetIds
    this.report.includeAllFundFactSheets = advice.includeAllFundFactSheets
    this.report.customer.name = `${customer.given_name} ${customer.family_name}`
    this.report.customer.personalNumber = customer.sub
    this.report.customer.email = customer.email
    this.report.customer.mobilePhone = customer.mobile
    this.report.advisorNotes = advice.notes?.adviceNote ?? ''
    this.report.rtqAnswers = rtq

    this.timeToPrint.next(true)

    // Get all the graphs for the report
    forkJoin(this.graphs.map(f => f.draw()))
      .pipe(
        // We have graphs in the accounts that we don't want so we remove them here
        map(images => images.filter(image => image.id !== 'unusedOriginal' as TImageId)),
        switchMap(images => {
          this.images = images
          // Get all the data for the report
          return forkJoin(this.savingsGoals.map(s => s.getData()))
        }),
        switchMap(documentData => {
          this.images.forEach(image => {
            const savingsGoal = documentData.find(p => p.savingsGoalId === image.savingsGoalId)
            if (savingsGoal) {
              savingsGoal[image.id].image = image.image
            }
          })
          this.report.savingsGoals = documentData
          this.timeToPrint.next(false)
          return this.print(this.report)
        })
      )
      .subscribe({
        next: (data) => {
          this.document = data
          this.documentUrl.set(data.url)
          this.generatingDocument.set(false)
        }
      })
  }

  public print(documentData: any) {
    const url = `${environment.irApiUrl}/print`
    return this.http.put<IPrintResult>(url, documentData)
  }

  public signDocument(signingMethod: string, customerId: string, adviceId: string){
    const url = `${environment.irApiUrl}/customers/${customerId}/advices/${adviceId}/documents`
    return this.http.post<void>(url, {documentId: this.document!.id, signingMethod: signingMethod})
  }

  public getDocument(customerId: string, adviceId: string): Observable<IDocument> {
    const url = `${environment.irApiUrl}/customers/${customerId}/advices/${adviceId}/documents`
    return this.http.get<IDocument[]>(url).pipe(
      map(document => {
        return {
          ...document[0],
          adviceId: adviceId,
        }
      })
    )
  }

  public getDocumentSignedUrl(customerId: string, adviceId: string, documentId: string) {
    const url = `${environment.irApiUrl}/customers/${customerId}/advices/${adviceId}/documents/${documentId}/download`
    return this.http.get<ISignedUrl>(url)
  }
}

